import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { z } from 'zod'
import { BASKET_API, BasketApi } from 'components/ordering/multiple-baskets/basket-api/basket-api.js'
import {
  GetPossibleTransferBasketRecipientsRequest,
} from 'components/ordering/multiple-baskets/basket-api/get-possible-transfer-basket-recipients.js'
import { NameBasketRequest } from 'components/ordering/multiple-baskets/basket-api/name-basket.js'
import { TransferSelectedBasketRequest } from 'components/ordering/multiple-baskets/basket-api/transfer-selected-basket.js'
import { User } from 'components/ordering/multiple-baskets/basket-api/user.js'
import { BasketTransferFormResetEvent } from 'components/ordering/multiple-baskets/basket-events.js'
import {
  BASKET_LIST_MUTATOR,
  BASKET_LIST_STORE,
  BasketListMutator,
  BasketListStore,
} from 'components/ordering/multiple-baskets/basket-store/basket-list-store.js'
import { style } from './basket-transfer-form.style.js'
import { TransferBasketRecipient } from 'components/ordering/acl/internal-types.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


const transferSchema = z.object({
  name: z.string().min(1),
  recipient: z.number(),
})

type Transfer = z.infer<typeof transferSchema>

type Validity = {
  key: keyof Transfer,
  valid: boolean,
  message: string,
}

type Translation = {
  key: string,
  invalid: string,
}


@customElement('csc-basket-transfer-form')
export class CSCBasketTransferForm extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ attribute: 'ordering-endpoint' })
  orderingEndpoint = ''

  @property({ type: Number, attribute: 'shop-id' })
  shopId = 0

  @property({ type: Number, attribute: 'party-id' })
  partyId = 0

  @property({ type: Number, attribute: 'user-id' })
  userId: number | null = null

  private get user(): User {
    if (this.userId !== null && this.partyId !== 0 && this.shopId !== 0) {
      return { userId: this.userId, shopId: this.shopId, partyId: this.partyId }
    }
    throw new Error('user-id, party-id and shop-id are mandatory.')
  }

  @state()
  basketName: string = ''

  @inject(BASKET_API)
  private basketApi!: BasketApi

  @inject(BASKET_LIST_MUTATOR)
  private basketListMutator!: BasketListMutator

  @inject(BASKET_LIST_STORE)
  private basketListStore!: BasketListStore

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @state()
  private disableSubmitButton = false

  @state()
  private selectedBasketId: string = ''

  @state()
  recipients: TransferBasketRecipient[] = []

  @query('form')
  form!: HTMLFormElement

  @state()
  validity: Validity[] = [
    {
      key: 'name',
      valid: true,
      message: '',
    }, {
      key: 'recipient',
      valid: true,
      message: '',
    },
  ]

  // Kettingzaag voor de Rolf.
  translations: Record<string, Translation> = {
    name: {
      key: 'naam',
      invalid: t('basket_name_invalid', { ns: 'csc-basket-transfer-form', default: 'You must enter a name' }).toString(),
    },
    recipient: {
      key: 'ontvanger',
      invalid: t('recipient_invalid', { ns: 'csc-basket-transfer-form', default: 'You must specify a recipient' }).toString(),
    },
  }

  private formReset = () => {
    this.dispatchEvent(new BasketTransferFormResetEvent())
  }

  private formSubmitted = async (event: SubmitEvent) => {
    event.preventDefault()
    this.disableSubmitButton = true

    const form = new FormData(this.form)

    try {
      const transfer = transferSchema.parse({
        name: form.get('basket-name'),
        recipient: parseInt(form.get('recipient-user-id')?.toString() || ''),
      })

      this.checkValidation(null)

      const nameBasketRequest = new NameBasketRequest(
        this.orderingEndpoint,
        this.selectedBasketId,
        transfer.name,
      )

      const transferSelectedBasketRequest = new TransferSelectedBasketRequest(
        this.orderingEndpoint,
        transfer.recipient,
        this.shopId,
        this.selectedBasketId,
      )

      await this.basketApi.nameBasket(nameBasketRequest)
      await this.basketApi.transferSelectedBasket(transferSelectedBasketRequest)

      this.windowLocation.reload()
    } catch (e: unknown) {
      this.disableSubmitButton = false
      this.checkValidation(e)
    }
  }

  private updateValidityField(issue: z.core.$ZodIssue | undefined) {
    if (issue) {
      return {
        valid: false,
        message: this.getMessage(issue),
      }
    } else {
      return {
        valid: true,
        message: '',
      }
    }
  }

  private getMessage(issue: z.core.$ZodIssue) {
    const pathKey = issue.path[0]
    if (typeof pathKey !== 'string' && typeof pathKey !== 'number') {
      return issue.message
    }

    const translation = this.translations[pathKey]

    if (!translation) {
      return issue.message
    }

    return translation.invalid
  }

  private checkValidation(e: unknown) {
    if (e instanceof z.ZodError) {
      this.validity = this.validity.map(field => {
        const issue = e.issues.find(issue => issue.path.includes(field.key))
        return {
          key: field.key,
          ...this.updateValidityField(issue),
        }
      })
    } else {
      this.validity = this.validity.map(field => {
        return {
          ...field,
          valid: true,
        }
      })
    }
  }

  override async connectedCallback() {
    super.connectedCallback()

    createEffect(() => {
      const selectedBasket = this.basketListStore.selectedBasket()
      if (selectedBasket === null) { return }

      this.selectedBasketId = selectedBasket.basketId
      this.basketName = selectedBasket.name ?? ''
    })

    const recipientsResponse = await this.basketApi.getPossibleTransferBasketRecipients(
      new GetPossibleTransferBasketRecipientsRequest(this.orderingEndpoint, this.shopId, this.partyId)
    )
    this.recipients = recipientsResponse.recipients

    this.basketListMutator.initialize(this.user)
  }

  private renderNameField = (): TemplateResult => {
    return html`
      <cse-form-field>
        <cse-label slot="label" for="basket-name">
          ${t('basket_name_label', { ns: 'csc-basket-transfer-form', default: 'Naam winkelwagen' })}
        </cse-label>
        <cse-text-input
          name="basket-name"
          placeholder="${t('basket_name_placeholder', { ns: 'csc-basket-transfer-form', default: 'Basket name' })}"
          value="${this.basketName}">
        </cse-text-input>
        ${this.validity[0] && !this.validity[0].valid
          ? html`
            <div id="invalid-name" slot="invalid-text">
              ${this.validity[0].message}
            </div>`
          : nothing}
      </cse-form-field>
    `
  }

  private renderRecipientField = (): TemplateResult => {
    return html`
      <cse-form-field>
        <cse-label slot="label" for="recipient-user-id">
          ${t('recipient_label', { ns: 'csc-basket-transfer-form', default: 'User' })}
        </cse-label>
        <cse-select
          name="recipient-user-id"
          placeholder="${t('recipient_placeholder', { ns: 'csc-basket-transfer-form', default: 'Select a user' })}">
          ${this.recipients.map((recipient) =>
            html`
              <cse-option value="${recipient.userId}">
                ${recipient.name} (${recipient.emailAddress})
              </cse-option>
            `,
          )}
        </cse-select>
        ${this.validity[1] && !this.validity[1].valid
          ? html`
            <div id="invalid-recipient" slot="invalid-text">
              ${this.validity[1].message}
            </div>`
          : nothing}
      </cse-form-field>
    `
  }

  protected override render(): TemplateResult {
    return html`
      <form @submit="${this.formSubmitted}" @reset="${this.formReset}">
        <cse-fieldset class="basket-container">
          ${this.renderNameField()}
          ${this.renderRecipientField()}
        </cse-fieldset>

        <div class="basket-buttons">
          <cse-button
            variant="default"
            type="reset"
            fit="fill"
            id="do-reset">
            ${t('cancel_button', { ns: 'csc-basket-transfer-form', default: 'Cancel' })}
          </cse-button>

          <cse-button
            variant="primary"
            type="submit"
            fit="fill"
            id="do-transfer"
            ?disabled="${this.disableSubmitButton}">
            ${t('submit_button', { ns: 'csc-basket-transfer-form', default: 'Save and transfer' })}
          </cse-button>
        </div>
      </form>
    `
  }
}
